import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiPhoneCall,
  FiZap,
  FiChevronRight,
  FiTag,
} from 'react-icons/fi';
import Reveal from '../common/Reveal';
import { getProduct, slugify } from '../data/categories';

export default function ProductDetail() {
  const { categorySlug, index } = useParams();
  const data = getProduct(categorySlug, index);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [categorySlug, index]);

  if (!data) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-2xl flex-col items-center justify-center px-4 text-center">
        <FiZap size={32} className="text-slate-300" />
        <h1 className="mt-4 text-2xl font-extrabold text-navy">Product not found</h1>
        <p className="mt-2 text-slate-500">The product you&rsquo;re looking for may have moved. Take a look at our full catalogue instead.</p>
        <Link
          to="/products"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-brand-orangedark"
        >
          <FiArrowLeft /> Back to Products
        </Link>
      </div>
    );
  }

  const { category, item, overview, features, applications, specs, related, index: idx } = data;

  return (
    <div key={`${categorySlug}-${index}`} className="bg-white">
      {/* ---------------- BREADCRUMB ---------------- */}
      <div className="border-b border-slate-100 bg-slate-50">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-1.5 px-4 py-3.5 text-xs font-semibold text-slate-500 sm:px-6 lg:px-8">
          <Link to="/products" className="transition hover:text-brand-orange">
            Products
          </Link>
          <FiChevronRight size={12} />
          <Link to={`/products#${slugify(category.title)}`} className="transition hover:text-brand-orange">
            {category.title}
          </Link>
          <FiChevronRight size={12} />
          <span className="text-navy">{item.name}</span>
        </div>
      </div>

      {/* ---------------- MAIN ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
       <Link
  to={`/products#${slugify(category.title)}`}
  className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-brand-orange"
>
  <FiArrowLeft size={15} /> Back to {category.title}
</Link>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Image */}
          <Reveal direction="right">
            <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-3xl bg-slate-50 p-10 shadow-sm">
              {item.img ? (
                <img
                  src={item.img}
                  alt={item.name}
                  className="h-full w-full object-contain animate-fadeInUp"
                  loading="lazy"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-slate-300">
                  <FiZap size={40} />
                  <span className="text-xs font-semibold uppercase tracking-wide">Photo coming soon</span>
                </div>
              )}
            </div>
          </Reveal>

          {/* Content */}
          <Reveal direction="left" delay={120}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wide text-brand-orange">
              <FiTag size={12} /> {category.tag}
            </span>
            <h1 className="mt-4 text-2xl font-extrabold leading-tight text-navy sm:text-3xl lg:text-4xl">
              {item.name}
            </h1>
            <p className="mt-4 text-slate-600">{overview}</p>

            {/* Specs */}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-2">
              {specs.map((s) => (
                <div key={s.label} className="rounded-xl bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{s.label}</p>
                  <p className="mt-0.5 text-sm font-bold text-navy">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-bold text-white shadow-sm shadow-brand-orange/30 transition hover:-translate-y-0.5 hover:bg-brand-orangedark hover:shadow-md"
              >
                Enquire Now
                <FiArrowRight className="transition-transform group-hover:translate-x-1" size={15} />
              </Link>
              <a
                href="tel:+919958260035"
                className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3 text-sm font-bold text-navy transition hover:-translate-y-0.5 hover:border-brand-orange hover:text-brand-orange"
              >
                <FiPhoneCall size={15} /> +91 9958260035
              </a>
            </div>
          </Reveal>
        </div>

        {/* Features & Applications */}
        <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:gap-12">
          <Reveal direction="right" className="rounded-2xl bg-slate-50 p-6 sm:p-8">
            <h2 className="text-lg font-extrabold text-navy">Key Features</h2>
            <ul className="mt-4 space-y-2.5">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <FiCheckCircle className="mt-0.5 shrink-0 text-brand-orange" size={16} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal direction="left" delay={100} className="rounded-2xl bg-slate-50 p-6 sm:p-8">
            <h2 className="text-lg font-extrabold text-navy">Applications</h2>
            <ul className="mt-4 space-y-2.5">
              {applications.map((a) => (
                <li key={a} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <FiCheckCircle className="mt-0.5 shrink-0 text-brand-orange" size={16} />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* ---------------- RELATED PRODUCTS ---------------- */}
        {related.length > 0 && (
          <div className="mt-16">
            <Reveal>
              <h2 className="text-xl font-extrabold text-navy sm:text-2xl">More from {category.title}</h2>
            </Reveal>
            <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-4">
              {category.items.map((rItem, rIdx) => {
                if (rIdx === idx) return null;
                return (
                  <Reveal key={rItem.name + rIdx} delay={(rIdx % 4) * 70}>
                    <Link
                      to={`/products/${categorySlug}/${rIdx}`}
                      className="group relative block overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition duration-300 hover:-translate-y-1.5 hover:border-brand-orange/30 hover:shadow-xl"
                    >
                      <div className="relative flex aspect-square items-center justify-center overflow-hidden bg-slate-50/70 p-5">
                        {rItem.img ? (
                          <img
                            src={rItem.img}
                            alt={rItem.name}
                            className="h-full w-full object-contain transition duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                        ) : (
                          <FiZap size={28} className="text-slate-300" />
                        )}
                      </div>
                      <div className="border-t border-slate-100 px-3.5 py-3">
                        <p className="line-clamp-2 text-xs font-semibold leading-snug text-slate-700 sm:text-sm">
                          {rItem.name}
                        </p>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
